import { LogLevel, PublicClientApplication, type Configuration } from '@azure/msal-browser';
import { config } from '../config';

const TENANT_ID = config.entraTenantId;
const SPA_CLIENT_ID = config.entraSpaClientId;
const API_CLIENT_ID = config.entraApiClientId;

/**
 * Der Scope unserer eigenen API.
 *
 * Nicht zu verwechseln mit Graph-Scopes: Dieses Token ist ausschließlich für
 * unsere API bestimmt, und genau das prüft sie über den `aud`-Claim.
 */
export const API_SCOPE = `api://${API_CLIENT_ID}/access_as_user`;

const msalConfig: Configuration = {
  auth: {
    clientId: SPA_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    /**
     * Zeigt auf die Bridge-Seite, nicht auf die Anwendung. Muss exakt — inklusive
     * Protokoll, Port und Pfad — mit dem Eintrag in der App-Registrierung
     * übereinstimmen.
     */
    redirectUri: `${window.location.origin}/redirect.html`,
    postLogoutRedirectUri: `${window.location.origin}/redirect.html`,
  },
  cache: {
    /**
     * sessionStorage statt localStorage: Der Zugriffsradius eines
     * XSS-Vorfalls bleibt damit auf den einen Tab und die eine Sitzung begrenzt.
     * Der Preis ist, dass ein neuer Tab sich erneut anmeldet — bei einer
     * internen Anwendung ein guter Tausch.
     *
     * `storeAuthStateInCookie` und `navigateToLoginRequestUrl` gibt es in v5
     * nicht mehr: Beide waren Behelfe für die alten Popup- und Iframe-Abläufe,
     * die durch die Redirect-Bridge ersetzt wurden.
     */
    cacheLocation: 'sessionStorage',
  },
  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) console.error('[MSAL]', message);
        else if (level === LogLevel.Warning) console.warn('[MSAL]', message);
      },
    },
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

/** Ist Entra überhaupt konfiguriert? Solange nicht, läuft die App im Demo-Modus. */
export const isAuthConfigured =
  Boolean(TENANT_ID) &&
  Boolean(SPA_CLIENT_ID) &&
  Boolean(API_CLIENT_ID) &&
  !TENANT_ID.startsWith('00000000');

/**
 * MSAL muss vor jeder anderen Nutzung initialisiert werden — in v5 ist das
 * asynchron und nicht mehr optional.
 */
export async function initializeMsal(): Promise<void> {
  await msalInstance.initialize();

  // Nach einem Redirect-Login liegt die Antwort noch in der URL.
  const result = await msalInstance.handleRedirectPromise();
  if (result?.account) {
    msalInstance.setActiveAccount(result.account);
    return;
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]!);
  }
}
